import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Package, Search, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { api, eur } from "@/lib/api";
import Reveal from "@/components/Reveal";

export default function OrderTrack() {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [searching, setSearching] = useState(false);
  const [review, setReview] = useState({ product_slug: "", rating: 5, text: "" });
  const [reviewSent, setReviewSent] = useState(false);
  const [reviewErr, setReviewErr] = useState(false);

  const [params] = useSearchParams();

  const fetchOrder = async (q) => {
    setSearching(true);
    setNotFound(false);
    setOrder(null);
    try {
      const res = await api.order(q.trim().toUpperCase());
      setOrder(res);
      setReviewSent(false);
      setReview({ product_slug: res.items[0]?.slug || "", rating: 5, text: "" });
    } catch {
      setNotFound(true);
    } finally {
      setSearching(false);
    }
  };

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    fetchOrder(query);
  };

  useEffect(() => {
    const q = (params.get("order") || "").trim();
    if (q) {
      setQuery(q.toUpperCase());
      fetchOrder(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="track-page" className="bg-white">
      <section className="relative overflow-hidden border-b border-navy/10 bg-surface pb-16 pt-28">
        <span aria-hidden className="text-ghost pointer-events-none absolute -bottom-8 end-0 select-none font-display text-[14vw] italic leading-none">
          Orders
        </span>
        <div className="relative mx-auto max-w-7xl px-6">
          <Reveal><p className="label-luxe text-gold-dark">{t.track.kicker}</p></Reveal>
          <Reveal delay={1}>
            <h1 className="mt-5 font-display text-5xl font-light tracking-tight text-navy sm:text-6xl">{t.track.title}</h1>
          </Reveal>
          <Reveal delay={2}>
            <p className="mt-5 max-w-lg text-sm font-light text-navy/50">{t.track.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-2xl px-6 py-20">
        <Reveal>
          <form onSubmit={search} data-testid="track-form" className="flex flex-col gap-4 sm:flex-row">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.track.ph}
              data-testid="track-input"
              className="input-luxe flex-1 font-mono tracking-wider"
            />
            <button type="submit" data-testid="track-search-button" disabled={searching} className="btn-primary-luxe shrink-0 disabled:opacity-50">
              <Search size={14} />
              {searching ? t.common.loading : t.track.btn}
            </button>
          </form>
        </Reveal>

        {notFound && (
          <Reveal className="mt-8">
            <p data-testid="track-not-found" className="border border-red-200 bg-red-50 px-6 py-4 text-sm text-red-700">
              {t.track.notFound}
            </p>
          </Reveal>
        )}

        {order && (
          <Reveal className="mt-10">
            <div data-testid="track-result" className="border border-navy/10 bg-white p-8">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package size={20} strokeWidth={1.25} className="text-gold-dark" />
                  <span data-testid="track-order-id" className="font-mono text-lg font-semibold text-navy">{order.id}</span>
                </div>
                <span data-testid="track-order-status" className="border border-sage/50 bg-sage-light/60 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-sage-dark">
                  {order.status === "paid" ? t.track.paid : order.status}
                </span>
              </div>
              <div className="mt-6 space-y-2 border-t border-navy/10 pt-6">
                {order.items.map((i) => (
                  <div key={i.slug} className="flex justify-between text-sm text-navy/70">
                    <span>{i.name} × {i.qty}</span>
                    <span>{eur(i.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 space-y-1.5 border-t border-navy/10 pt-4 text-sm text-navy/60">
                <div className="flex justify-between">
                  <span>{t.cart.shipping}</span>
                  <span>{order.shipping === 0 ? t.cart.free : eur(order.shipping)}</span>
                </div>
                <div className="flex justify-between pt-2 text-base font-semibold text-navy">
                  <span>{t.cart.total}</span>
                  <span data-testid="track-order-total">{eur(order.total)}</span>
                </div>
              </div>
              <div className="mt-6 border-t border-navy/10 pt-6">
                <p className="label-luxe text-navy/40">{t.track.fulfillmentL}</p>
                <div className="mt-4 flex items-center" data-testid="track-fulfillment-timeline">
                  {["preparing", "shipped", "delivered"].map((step, i, arr) => {
                    const activeIdx = arr.indexOf(order.fulfillment || "preparing");
                    return (
                      <div key={step} className="flex flex-1 items-center">
                        <div className="flex flex-col items-center">
                          <span className={`h-2.5 w-2.5 rounded-full ${i <= activeIdx ? "bg-gold" : "bg-navy/15"}`} />
                          <span data-testid={`track-step-${step}`} className={`mt-2 text-[9px] font-semibold uppercase tracking-[0.12em] ${i <= activeIdx ? "text-gold-dark" : "text-navy/35"}`}>
                            {t.track[step]}
                          </span>
                        </div>
                        {i < arr.length - 1 && <div className={`mx-2 h-px flex-1 ${i < activeIdx ? "bg-gold" : "bg-navy/10"}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-8 border-t border-navy/10 pt-8">
                {reviewSent ? (
                  <p data-testid="review-sent" className="border border-sage/40 bg-sage-light/40 px-5 py-4 text-sm text-sage-dark">{t.rev.sentOk}</p>
                ) : (
                  <form
                    data-testid="review-form"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      setReviewErr(false);
                      try {
                        await api.submitReview({
                          order_id: order.id,
                          product_slug: review.product_slug,
                          name: order.customer?.name || "Customer",
                          rating: review.rating,
                          text: review.text,
                        });
                        setReviewSent(true);
                      } catch {
                        setReviewErr(true);
                      }
                    }}
                  >
                    <p className="label-luxe text-navy">{t.rev.formT}</p>
                    <p className="mt-2 text-xs font-light text-navy/45">{t.rev.formSub}</p>
                    <div className="mt-5 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="label-luxe mb-2 block text-navy/40">{t.rev.productL}</label>
                        <select
                          value={review.product_slug}
                          onChange={(e) => setReview({ ...review, product_slug: e.target.value })}
                          data-testid="review-product-select"
                          className="input-luxe"
                        >
                          {order.items.map((i) => (
                            <option key={i.slug} value={i.slug}>{i.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="label-luxe mb-2 block text-navy/40">{t.rev.ratingL}</label>
                        <div className="flex items-center gap-1.5 py-2">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <button
                              key={s}
                              type="button"
                              data-testid={`review-star-${s}`}
                              onClick={() => setReview({ ...review, rating: s })}
                              aria-label={`${s} stars`}
                            >
                              <Star size={20} className={s <= review.rating ? "fill-gold text-gold" : "text-navy/20 transition-colors hover:text-gold"} />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="label-luxe mb-2 block text-navy/40">{t.rev.textL}</label>
                      <textarea
                        required
                        rows={3}
                        value={review.text}
                        onChange={(e) => setReview({ ...review, text: e.target.value })}
                        data-testid="review-text-input"
                        className="input-luxe resize-none"
                      />
                    </div>
                    {reviewErr && <p data-testid="review-error" className="mt-3 text-xs text-red-600">{t.common.error}</p>}
                    <button type="submit" data-testid="review-submit-button" className="btn-outline-luxe mt-5">{t.rev.send}</button>
                  </form>
                )}
              </div>
              <div className="mt-6 border-t border-navy/10 pt-4 text-xs font-light text-navy/45">
                <span>{t.track.dateL}: {new Date(order.created_at).toLocaleDateString()}</span>
                {order.customer?.name && <span className="ms-4">{order.customer.name} — {order.customer.city}</span>}
              </div>
            </div>
          </Reveal>
        )}
      </section>
    </div>
  );
}
